import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useUserData } from "../../auth/useUserData.js";
import {
  usePdsSyncReviewAccess_ConnectionQuery,
  usePdsSyncReviewAccess_CountsQuery,
  usePdsSyncReviewAccess_LastSyncQuery,
  PdsSyncReviewAccess_CountsQuery,
} from "./PdsSyncReviewAccess.generated.js";

// Shared across both CRM tabs' banners/toolbar icons and the review modal's
// stats strip — a single instance of this hook is meant to be created by
// whichever CRM page is mounted (CrmPersons or CrmAll, only one at a time)
// and threaded down into the modal via props, so the counts query is never
// polled more than once per page (design D2 "badge polling fan-out" risk).
const COUNTS_POLL_INTERVAL_MS = 30_000;

export type PdsSyncTaskCounts = Extract<
  PdsSyncReviewAccess_CountsQuery["pdsSyncTaskCounts"],
  { __typename: "PdsSyncTaskCounts" }
>;

export interface PdsSyncReviewAccess {
  /** MANAGE_CRM + INTEGRATIONS + pds_sync connected — gates every PDS review surface (D2) */
  isEnabled: boolean;
  loading: boolean;
  counts: PdsSyncTaskCounts | null;
  /** Proxy for "last sync" — the most recently created task's timestamp (see PdsSyncReviewAccess_LastSync) */
  lastSyncAt: string | null;
  refetch: () => void;
}

export function usePdsSyncReviewAccess(): PdsSyncReviewAccess {
  const { hasOrganisationPermission, hasCapability } = useUserData();
  const client = useApolloClient();

  const hasGate =
    hasOrganisationPermission("MANAGE_CRM") && hasCapability("INTEGRATIONS");

  const connectionQuery = usePdsSyncReviewAccess_ConnectionQuery({
    client,
    skip: !hasGate,
    fetchPolicy: "cache-and-network",
  });

  const isConnected = Boolean(
    getDataOrNull(connectionQuery.data?.organisationIntegration)
      ?.organisationIntegration
  );

  const isEnabled = hasGate && isConnected;

  const countsQuery = usePdsSyncReviewAccess_CountsQuery({
    client,
    skip: !isEnabled,
    pollInterval: COUNTS_POLL_INTERVAL_MS,
    fetchPolicy: "cache-and-network",
  });

  const lastSyncQuery = usePdsSyncReviewAccess_LastSyncQuery({
    client,
    skip: !isEnabled,
    pollInterval: COUNTS_POLL_INTERVAL_MS,
    fetchPolicy: "cache-and-network",
  });

  return {
    isEnabled,
    loading: connectionQuery.loading || countsQuery.loading,
    counts: getDataOrNull(countsQuery.data?.pdsSyncTaskCounts),
    lastSyncAt:
      getDataOrNull(lastSyncQuery.data?.pdsSyncTasks)?.items[0]?.createdAt ??
      null,
    refetch: () => {
      void connectionQuery.refetch();
      void countsQuery.refetch();
      void lastSyncQuery.refetch();
    },
  };
}
